import Vue from 'vue';
import { store } from './store';
import router from './router.js';

import {
  convertErrorMessageForUI,
  deleteItemsFromLocalStorage,
  setItemsToLocalStorage,
  raiseError,
} from '@/services/functions';
import { getAccessTokenHandler } from '@/services/authorization/getAccessToken';
import { loginUserRequest, entraIdLoginRequest } from '@/services/authorization/login';
import { signUpResearcherRequest } from '@/services/authorization/signUpResearcher';
import { signUpCompanyRequest } from '@/services/authorization/signUpCompany';
import { sendForgotPasswordRequest } from '@/services/authorization/sendForgotPassword';
import { logoutRequest } from '@/services/authorization/logout';
import { confirmEmailRequest } from '@/services/authorization/confirmEmail';
import { changeForgotPasswordRequest } from '@/services/authorization/changeForgotPassword';
import { resendConfirmEmailRequest } from '@/services/authorization/resendConfirmEmail';
import { AuthMethodEnum, AUTH_METHOD_STORAGE_KEY } from '@/utils/constants/common';
import { ERROR_CODES } from '@/services/functions';
import { trackAuthSsoCompleted, trackAuthSsoFailed } from '@/services/analytics';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const AUTH_CLEAR_ERRORS_MUTATION = 'authentication/clearErrors';
const AUTH_SET_ERRORS_MUTATION = 'authentication/setErrors';
const AUTH_ERRORS_GROUP = 'authErrors';
const AUTH_OTP_ERRORS_GROUP = 'authOtpErrors';
const SIGN_IN_INPUT_ERROR_CLASS = 'sign-in__input--error';

/** Extracts and converts the error message from the response (without writing to store). */
function getConvertedAuthError(error) {
  if (!error?.response) {
    return convertErrorMessageForUI(ERROR_CODES.SERVER_UNAVAILABLE);
  }
  const status = error.response.status;
  const data = error.response.data;

  if (status === 504 || (status >= 500 && !data?.code && !data?.errorMessage)) {
    return convertErrorMessageForUI(ERROR_CODES.SERVER_UNAVAILABLE);
  }

  const msg = data?.code ? data : data?.errorMessage;
  return convertErrorMessageForUI(msg);
}

/**
 * Writes the error to store and highlights the given form fields.
 * @param {{ key: string, body: string }} converted — object { key, body } for store
 * @param {string[]} fields — field ids for raiseError (e.g. ['password'], ['otp'], ['email','password'])
 */
function applyAuthError(converted, fields) {
  store.commit(AUTH_SET_ERRORS_MUTATION, converted);
  const group = converted.key === 'otp' ? AUTH_OTP_ERRORS_GROUP : AUTH_ERRORS_GROUP;
  fields.forEach((f) => raiseError(f, group, SIGN_IN_INPUT_ERROR_CLASS));
}

function handleAuthError(error, fields = ['email', 'password']) {
  const converted = getConvertedAuthError(error);
  applyAuthError(converted, fields);
  return converted;
}

/**
 * Minimal signup error handler (keeps previous behavior):
 * - network error => default message
 * - otherwise convert response message and commit
 * - highlight only email field if key === 'email'
 */
function handleSignUpError(error) {
  if (!error?.response) {
    const converted = convertErrorMessageForUI(ERROR_CODES.SERVER_UNAVAILABLE);

    store.commit(AUTH_SET_ERRORS_MUTATION, converted);
    return converted;
  }

  const data = error.response.data || {};
  const msg = data?.code ? data : data.errorMessage || data.message || data.error || data;

  const converted = convertErrorMessageForUI(msg);

  store.commit(AUTH_SET_ERRORS_MUTATION, converted);

  if (converted?.key === 'email') {
    raiseError('email', AUTH_ERRORS_GROUP, SIGN_IN_INPUT_ERROR_CLASS);
  }

  return converted;
}

/**
 * Commit a simple response message error or fallback when response is missing.
 * @param {unknown} error
 */
function commitSimpleAuthError(error) {
  if (!error?.response?.data) {
    store.commit(AUTH_SET_ERRORS_MUTATION, ERROR_CODES.SERVER_UNAVAILABLE);
    return;
  }
  store.commit(AUTH_SET_ERRORS_MUTATION, convertErrorMessageForUI(error.response.data?.message));
}

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

export const useKeycloakAuth = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        popupOpen: false,
        error: null,
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      try {
        this.isAuthenticated = !!(await getAccessTokenHandler());
        const authMethod = localStorage.getItem(AUTH_METHOD_STORAGE_KEY);
        if (this.isAuthenticated && authMethod === AuthMethodEnum.SSO) {
          trackAuthSsoCompleted(authMethod);
        }
      } catch (err) {
        this.isAuthenticated = false;
        const authMethod = localStorage.getItem(AUTH_METHOD_STORAGE_KEY);
        if (authMethod === AuthMethodEnum.SSO) {
          trackAuthSsoFailed(authMethod, err?.message || 'unknown');
        }
      } finally {
        this.loading = false;
      }
    },
    methods: {
      loginWithRedirect({ appState }) {
        // if we went from acceptInvite component
        if (appState.targetUrl.includes('/accept-invite')) {
          const invitedId = appState.targetUrl.split('/').pop();
          router.push({
            name: 'NotAuthenticated',
            query: { id: invitedId },
          });
        } else if (appState.redirect) {
          router.push({
            name: 'SignIn',
            query: { redirect: appState.redirect },
          });
        } else {
          const query =
            appState && appState.targetUrl !== 'scans' ? { redirect: appState.targetUrl } : {};
          router.push({ name: 'SignIn', query });
        }
      },
      async login(payload) {
        store.commit(AUTH_CLEAR_ERRORS_MUTATION);
        try {
          const response = await loginUserRequest(payload);

          this.isAuthenticated = true;
          return response;
        } catch (error) {
          const converted = getConvertedAuthError(error);
          const fields = converted.key === 'otp' ? ['otp'] : ['email', 'password'];
          applyAuthError(converted, fields);
          return null;
        }
      },
      async signUpResearcher(payload) {
        store.commit(AUTH_CLEAR_ERRORS_MUTATION);
        try {
          return await signUpResearcherRequest(payload);
        } catch (error) {
          handleSignUpError(error);
          return null;
        }
      },
      async signUpCompany(payload) {
        store.commit(AUTH_CLEAR_ERRORS_MUTATION);
        try {
          await signUpCompanyRequest(payload);
        } catch (error) {
          handleSignUpError(error);
          return null;
        }
      },
      async sendForgotPassword(payload) {
        store.commit(AUTH_CLEAR_ERRORS_MUTATION);
        try {
          const res = await sendForgotPasswordRequest(payload);
          return res?.data;
        } catch (error) {
          commitSimpleAuthError(error);
        }
      },
      async confirmEmail(payload) {
        try {
          await confirmEmailRequest(payload);
          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'sign_up_confirmed', {
              event_category: 'engagement',
              event_label: 'custom',
              signature: payload.id,
            });
          }
        } catch (error) {
          console.log(error, 'error');
        }
      },
      async resendConfirmEmail(payload) {
        try {
          await resendConfirmEmailRequest(payload);
        } catch (error) {
          commitSimpleAuthError(error);
        }
      },
      async changePassword(payload) {
        try {
          await changeForgotPasswordRequest(payload);
        } catch (error) {
          console.log(error, 'error');
        }
      },

      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently() {
        return await getAccessTokenHandler();
      },

      /** Logs the user out and removes their session on the authorization server */
      async logout() {
        const keysToClear = [
          'userType',
          'integrationsCount',
          AUTH_METHOD_STORAGE_KEY,
          'accessToken',
          'refreshToken',
          'idToken',
        ];
        const clearLocalData = () => {
          deleteItemsFromLocalStorage(keysToClear);
          this.isAuthenticated = false;
        };

        const authMethod = localStorage.getItem(AUTH_METHOD_STORAGE_KEY);

        if (authMethod === AuthMethodEnum.SSO) {
          clearLocalData();
          window.location.href = `${process.env.VUE_APP_AUTH_BASE_URL}/sso/logout`;
          return;
        }

        try {
          await logoutRequest();
        } catch (error) {
          console.log('Regular logout failed, continuing cleanup', error);
        } finally {
          clearLocalData();
          await router.push({ name: 'Root' });
          router.go();
        }
      },
      async authHandle() {
        await this.$store.commit('user/isAuthenticated', true);
      },
      async forbidden(query) {
        await router.push({
          name: 'Forbidden',
          query: { redirect: query },
        });
      },
    },
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const AuthPlugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useKeycloakAuth(options);
  },
};
